import {
    AppCard,
    Button,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    SearchInput,
    Switch,
} from "@jhool-io/fe-components";
import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import moment from "moment";
import { useAppSelector } from "../../../../../hooks/useRedux";
import FilterButton from "../../../../../shared-ui/Buttons/FilterButton/FilterButton";
import {
    displayNameInRightFormat,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    truncateString,
} from "../../../../../utils/helpers";
import NavigateLeft from "../../../../../components/Icons/NavigateLeft";
import NavigateRight from "../../../../../components/Icons/NavigateRight";
import CaseloadList from "../CaseloadList/CaseloadList";
import SuperviseeCaseloadList from "../SuperviseeCaseloadList/SuperviseeCaseloadList";
import MyWeekView from "../MyWeekView/MyWeekView";
import { useFetchClientList } from "../../../../../hooks/queries/client";
import TableFiltersDisplay from "../../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import useNewBackground from "../../../../../hooks/useNewBackground";

export default function CaseloadAndCalendarWrapper() {
    const [currentWeek, setCurrentWeek] = useState(moment().startOf("isoWeek"));
    const [searchParams, setSearchParams] = useSearchParams();

    const { practice } = useAppSelector((state) => state.userPractice);

    const useNewBg = useNewBackground();

    const tabInUrl = searchParams.get("view") || "week";

    const filter = searchParams.get("filter");
    const showPausedFilter = searchParams.get("show_paused");
    const clientFilter = searchParams.getAll("client_ids[]") || [];
    const searchFilter = searchParams.get("search") || "";

    const showPausedClientFilterTextToShow = () => {
        if (showPausedFilter === "true") {
            return "Paused clients";
        }

        if (showPausedFilter === "false") {
            return "Unpaused clients";
        }

        return "Show all appointments";
    };

    const startOfWeek = currentWeek.clone().startOf("isoWeek").format("MMM D");
    const endOfWeek = currentWeek
        .clone()
        .endOf("isoWeek")
        .format("MMM D, YYYY");

    const currentWeekRange = `${startOfWeek} - ${endOfWeek}`;

    const { data: clientData } = useFetchClientList({
        page: 1,
        provider_id: practice?.provider_id as string,
    });

    const filteredClientNames = clientData?.data
        .filter((client) => clientFilter.includes(client.client_id))
        .map((client) =>
            displayNameInRightFormat({
                firstName: client.first_name,
                lastName: client.last_name,
            })
        );

    const handleTabSwitch = (tab: string) => {
        searchParams.set("view", tab);

        setSearchParams(`view=${tab}`);
    };

    const switchOptions = [
        {
            key: "week",
            title: "Week",
            onClick: () => {
                handleTabSwitch("week");
                searchParams.set(
                    "from_date",
                    currentWeek.clone().startOf("isoWeek").format("YYYY-MM-DD")
                );
                searchParams.set(
                    "to_date",
                    currentWeek.clone().endOf("isoWeek").format("YYYY-MM-DD")
                );
                setSearchParams(searchParams);
            },
        },
        {
            key: "my_caseload",
            title: "My Caseload",
            onClick: () => handleTabSwitch("my_caseload"),
        },
        {
            key: "supervisee_caseload",
            title: "Supervisee’s CL",
            onClick: () => handleTabSwitch("supervisee_caseload"),
            hidden: !practice?.is_supervisor,
        },
    ];

    const updateWeekRange = (direction: string) => {
        const newWeek =
            direction === "next"
                ? currentWeek.clone().add(1, "week")
                : currentWeek.clone().subtract(1, "week");
        searchParams.set(
            "from_date",
            newWeek.clone().startOf("isoWeek").format("YYYY-MM-DD")
        );
        searchParams.set(
            "to_date",
            newWeek.clone().endOf("isoWeek").format("YYYY-MM-DD")
        );
        setSearchParams(searchParams);
        setCurrentWeek(newWeek);
    };

    const handleSetFilter = (filterType: string) => {
        if (filterType === "") searchParams.delete("filter");
        else searchParams.set("filter", filterType);

        setSearchParams(searchParams);
    };
    const handleSetPausedClientsFilter = (showPaused: string) => {
        if (showPaused === "") searchParams.delete("show_paused");
        else searchParams.set("show_paused", showPaused);

        setSearchParams(searchParams);
    };

    const handleClientFilter = (client_id: string) => {
        if (clientFilter.includes(client_id)) {
            searchParams.delete("client_ids[]", client_id);
        } else {
            searchParams.append("client_ids[]", client_id);
        }
        setSearchParams(searchParams);
    };

    const handleSearchFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === "") searchParams.delete("search");
        else searchParams.set("search", e.target.value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    return (
        <AppCard
            mode={useNewBg ? "new" : "old"}
            className="rounded-r12"
            oldCardClass="bg-white border border-[rgba(231,231,231,0.91)]"
        >
            <div
                className={`flex items-center justify-between gap-x-24 p-16 flex-wrap gap-y-16 border-b overflow-auto md:overflow-x-hidden ${
                    useNewBg
                        ? "border-b-[rgba(255,255,255,0.30)]"
                        : "border-b-strokedark"
                }`}
            >
                <div>
                    {tabInUrl === "week" ? (
                        <>
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton
                                        text="Client"
                                        mode={useNewBg ? "new" : "old"}
                                    />
                                </DropdownTrigger>
                                <DropdownContent
                                    width="auto"
                                    maxHeight={216}
                                    className="overflow-y-auto"
                                    mode={useNewBg ? "new" : "old"}
                                >
                                    {clientData?.data?.map((client) => (
                                        <DropdownItem
                                            key={client.client_id}
                                            onClick={() =>
                                                handleClientFilter(
                                                    client.client_id
                                                )
                                            }
                                            isMulti
                                            itemValue={client.client_id}
                                            values={clientFilter}
                                            closeMenuOnSelect
                                        >
                                            {displayNameInRightFormat({
                                                firstName: client.first_name,
                                                lastName: client.last_name,
                                            })}
                                        </DropdownItem>
                                    ))}
                                </DropdownContent>
                            </Dropdown>
                            <TableFiltersDisplay
                                appliedFilters={[
                                    {
                                        key: "client_ids[]",
                                        values: filteredClientNames as string[],
                                    },
                                ]}
                                wrapperClass="mt-12"
                            />
                        </>
                    ) : (
                        <SearchInput
                            placeholder="Search by client name"
                            containerClass="w-[250px]"
                            onChange={handleSearchFilter}
                            defaultValue={searchFilter}
                            mode={useNewBg ? "new" : "old"}
                        />
                    )}
                </div>
                <div className="flex items-center gap-x-24 flex-wrap gap-y-16">
                    {tabInUrl === "my_caseload" ? (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton
                                    text={
                                        filter
                                            ? makeStringFirstLetterCapital(
                                                  truncateString(
                                                      removeEnumUnderscore(
                                                          filter
                                                      )
                                                  )
                                              )
                                            : "Show All Clients"
                                    }
                                    mode={useNewBg ? "new" : "old"}
                                />
                            </DropdownTrigger>
                            <DropdownContent
                                width="auto"
                                align="end"
                                mode={useNewBg ? "new" : "old"}
                            >
                                <DropdownItem
                                    className="capitalize flex gap-x-8 items-center text-xs font-medium"
                                    onClick={() => handleSetFilter("")}
                                    isSelected={!filter}
                                >
                                    All clients
                                </DropdownItem>
                                <DropdownItem
                                    className="capitalize flex gap-x-8 items-center text-xs font-medium"
                                    onClick={() =>
                                        handleSetFilter(
                                            "clients_with_pending_task"
                                        )
                                    }
                                    isSelected={
                                        filter === "clients_with_pending_task"
                                    }
                                >
                                    Clients with pending tasks
                                </DropdownItem>
                            </DropdownContent>
                        </Dropdown>
                    ) : null}
                    {tabInUrl === "my_caseload" ? (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton
                                    text={showPausedClientFilterTextToShow()}
                                    mode={useNewBg ? "new" : "old"}
                                />
                            </DropdownTrigger>
                            <DropdownContent
                                align="end"
                                mode={useNewBg ? "new" : "old"}
                            >
                                <DropdownItem
                                    className="capitalize flex gap-x-8 items-center text-xs font-medium"
                                    onClick={() =>
                                        handleSetPausedClientsFilter("")
                                    }
                                    isSelected={!showPausedFilter}
                                >
                                    All clients
                                </DropdownItem>
                                <DropdownItem
                                    className="capitalize flex gap-x-8 items-center text-xs font-medium"
                                    onClick={() =>
                                        handleSetPausedClientsFilter("false")
                                    }
                                    isSelected={showPausedFilter === "false"}
                                >
                                    Unpaused clients
                                </DropdownItem>
                                <DropdownItem
                                    className="capitalize flex gap-x-8 items-center text-xs font-medium"
                                    onClick={() =>
                                        handleSetPausedClientsFilter("true")
                                    }
                                    isSelected={showPausedFilter === "true"}
                                >
                                    Paused clients
                                </DropdownItem>
                            </DropdownContent>
                        </Dropdown>
                    ) : null}

                    {tabInUrl === "week" ? (
                        <div
                            className={`flex items-center gap-x-16 text-sm font-medium ${
                                useNewBg ? "text-white" : "text-black"
                            } `}
                        >
                            <Button
                                variant="normal"
                                size="auto"
                                onClick={() => updateWeekRange("previous")}
                            >
                                <NavigateLeft
                                    fill={useNewBg ? "#FEF5E9" : undefined}
                                />
                            </Button>
                            {currentWeekRange}
                            <Button
                                variant="normal"
                                size="auto"
                                onClick={() => updateWeekRange("next")}
                            >
                                <NavigateRight
                                    fill={useNewBg ? "#FEF5E9" : undefined}
                                />
                            </Button>
                        </div>
                    ) : null}

                    <Switch
                        switchOptions={switchOptions}
                        activeSwitch={tabInUrl || "week"}
                        mode={useNewBg ? "new" : "old"}
                    />
                </div>
            </div>
            <div className="p-16">
                {tabInUrl === "week" ? <MyWeekView /> : null}
                {tabInUrl === "my_caseload" ? <CaseloadList /> : null}
                {tabInUrl === "supervisee_caseload" ? (
                    <SuperviseeCaseloadList />
                ) : null}
            </div>
        </AppCard>
    );
}
